import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Show header toolbar
   * @default true
   */
  showHeader: p.boolean(true),

  /**
   * Right sidebar width in pixels
   * @default 384 (w-96)
   */
  sidebarWidth: p.number(384),

  /**
   * Number of parameter fields in sidebar
   * @default 6
   */
  parameterCount: p.number(6),
} satisfies ComponentPropsOptions;

export type PlaygroundSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * PlaygroundSkeleton - Loading placeholder for playground pages
 *
 * Designed for LLM, TTS, and ASR playground pages with a consistent layout:
 * - Top header toolbar with selects and action buttons
 * - Two-column layout: large main area + narrow parameter sidebar
 *
 * @example
 * ```tsx
 * // Basic usage
 * <PlaygroundSkeleton />
 *
 * // Custom sidebar width
 * <PlaygroundSkeleton sidebarWidth={320} />
 *
 * // More parameters
 * <PlaygroundSkeleton parameterCount={8} />
 *
 * // With QueryLoader
 * <QueryLoader queries={[modelsQuery, pricingQuery]}>
 *   <QueryLoader.Loading>
 *     <PlaygroundSkeleton />
 *   </QueryLoader.Loading>
 *   <Card>
 *     ...
 *   </Card>
 * </QueryLoader>
 * ```
 */
const PlaygroundSkeleton = defineComponent({
  name: "PlaygroundSkeleton",
  props,
  setup(props) {
    return () => (
      <div class="flex h-[calc(100vh-5.25rem)] w-full flex-col divide-y rounded-lg border bg-card">
        {/* Header toolbar */}
        {props.showHeader && (
          <div class="flex h-12 flex-none items-center justify-between px-3">
            {/* Left side - Route/Model select */}
            <div class="flex items-center gap-2">
              <Skeleton class="h-9 w-48 rounded-md" />
            </div>

            {/* Right side - Action buttons */}
            <div class="flex items-center gap-2">
              <Skeleton class="h-9 w-32 rounded-md" />
              <Skeleton class="h-9 w-24 rounded-md" />
            </div>
          </div>
        )}

        {/* Main content area - Two columns */}
        <div class="flex grow divide-x overflow-hidden">
          {/* Left: Main workspace (messages, player, etc) */}
          <div class="flex grow flex-col space-y-4 overflow-hidden p-4">
            {/* Content area skeletons */}
            <Skeleton class="h-full w-full rounded" />
          </div>

          {/* Right: Parameters sidebar */}
          <div
            class="flex flex-none flex-col gap-4 overflow-hidden p-4"
            style={{ width: `${props.sidebarWidth}px`, maxWidth: "40%" }}
          >
            {/* Section header */}
            <div class="space-y-2">
              <Skeleton class="h-5 w-32 rounded" />
              <Skeleton class="h-3 w-full rounded" />
            </div>

            {/* Parameter fields */}
            {Array.from({ length: props.parameterCount }).map((_, i) => (
              <div key={`param-${i}`} class="space-y-2">
                <Skeleton class="h-4 w-24 rounded" />
                <Skeleton class="h-10 w-full rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
});

export default PlaygroundSkeleton;
